import React, { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { scrollElementIntoView, useScrollContainer } from '../utils/dom-functions'
import { getIsInWebView } from '../utils'
import { useForceRerender } from '../hooks'

interface ScrollRestoreProps {
  /** Will be appended to location. Used to separate scroll positions for the overview table. */
  customPath?: string
}

/**
 * Keeps track of scroll position based on the URL location pathname.
 * On mount, it the current page to the previously set scroll position.
 *
 * Add this component to a fully loaded page to have scroll restoration on that specific page.
 *
 * Also handles scrolling to elements if hash is in the url instead of the previous scroll potition.
 */
export const ScrollRestore: React.FC<ScrollRestoreProps> = ({ customPath }) => {
  const location = useLocation()
  const path = `${location.pathname}${customPath ?? ''}`
  /** Skip scroll restoration if there is a hash, since the app is trying to scroll to a specific part. */
  const hash = location.hash.slice(1)

  const scrollContainer = useScrollContainer()

  const isInIosWebView = getIsInWebView() === 'ios'

  const forceRerender = useForceRerender()

  useLayoutEffect(() => {
    if (!scrollContainer) return

    let timerId: NodeJS.Timeout
    if (isInIosWebView) {
      // Add a small delay on iOS so the layout is loaded
      timerId = setTimeout(() => scrollToLastPosition(scrollContainer, path, hash), 100)
    } else {
      scrollToLastPosition(scrollContainer, path, hash)
    }

    const savePosition =
      window.self === scrollContainer
        ? () => {
            saveScrollPosition(path, window.scrollY)
          }
        : (e: Event) => {
            const scrollTop = (e.target as HTMLElement)?.scrollTop ?? 0
            saveScrollPosition(path, scrollTop)
          }

    scrollContainer.addEventListener('scroll', savePosition)

    // Rerender to ensure the container is loaded and the scroll listener can be assigned.
    forceRerender()

    return () => {
      clearTimeout(timerId)
      scrollContainer.removeEventListener('scroll', savePosition)
    }
  }, [path, hash, scrollContainer, isInIosWebView, forceRerender])

  return null
}

const scrollToLastPosition = (scrollContainer: Window | HTMLElement, path: string, hash: string): void => {
  if (hash) {
    scrollElementIntoView(scrollContainer, hash)
  } else {
    scrollContainer.scrollTo({ top: getLastScrollPosition(path) })
  }
}

function getLastScrollPosition(path: string): number {
  const lastScrollPosition = localStorage.getItem(`position-${path}`)
  return Number(lastScrollPosition) || 0
}

const saveScrollPosition = (path: string, position: number): void => {
  localStorage.setItem(`position-${path}`, position.toString())
}
